import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { isDiscountActive, getDiscountPercentage } from '../config/discountConfig';
import './SummerSale.css';

// Change this one line when the sale end date moves.
const SALE_ENDS = '31 August';

/**
 * Thin announcement strip.
 * Place it at the very top of the home page, above the social icons.
 */
export const SaleStrip = () => {
  if (!isDiscountActive()) return null;

  const pct = getDiscountPercentage();

  return (
    <div className="sale-strip" role="status">
      <div className="sale-strip-track">
        <span className="sale-strip-item">
          <span className="sale-strip-seed" aria-hidden="true" />
          Summer sale — {pct} off everything
        </span>
        <span className="sale-strip-item">
          <span className="sale-strip-seed" aria-hidden="true" />
          Discount applied automatically at checkout
        </span>
        <span className="sale-strip-item">
          <span className="sale-strip-seed" aria-hidden="true" />
          Until {SALE_ENDS}
        </span>
      </div>
    </div>
  );
};

/**
 * The main sale block — a waffle-pressed discount stamp.
 * Place it between the hero section and the endorsement section.
 */
const SummerSale = () => {
  const [pressed, setPressed] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPressed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (!isDiscountActive()) return null;

  const pct = getDiscountPercentage();
  const digits = pct.replace('%', '');

  return (
    <section
      className={`sale-section ${pressed ? 'is-pressed' : ''}`}
      ref={sectionRef}
      aria-labelledby="sale-heading"
    >
      <div className="container">
        <div className="sale-card">
          <div className="sale-waffle" aria-hidden="true" />

          <div className="sale-card-body">
            <div className="sale-stamp">
              <span className="sale-stamp-digits">{digits}</span>
              <span className="sale-stamp-pct">%</span>
              <span className="sale-stamp-word">off</span>
            </div>

            <div className="sale-copy">
              <p className="sale-eyebrow">
                Summer sale
                <span className="sale-eyebrow-rule" aria-hidden="true" />
                ends {SALE_ENDS}
              </p>

              <h2 className="sale-title" id="sale-heading">
                Eat the cup.
                <br />
                Keep the change.
              </h2>

              <p className="sale-text">
                Every edible cup and every piece of cutlery is {pct} off. The price you see on
                the product page is already the sale price — no code to remember.
              </p>

              <div className="sale-actions">
                <Link to="/shop" className="sale-btn">
                  Shop the sale
                </Link>
                <Link to="/edible-cups" className="sale-link">
                  See the edible cups
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SummerSale;
